import { useState } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel, FormGroup, InputAdornment, IconButton,
  OutlinedInput, Autocomplete, TextField, createFilterOptions, Button, Box, Paper,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MapIcon from '@mui/icons-material/Map';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import TokenIcon from '@mui/icons-material/Token';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation, useTranslationKeys } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { prefixString, unprefixString } from '../common/util/stringUtils';
import SelectField from '../common/components/SelectField';
import useMapStyles from '../map/core/useMapStyles';
import useMapOverlays from '../map/overlay/useMapOverlays';
import { useCatch } from '../reactHelper';
import { sessionActions } from '../store';
import { useAdministrator, useRestriction } from '../common/util/permissions';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import PremiumTabs, { PremiumTabPanel } from '../common/components/PremiumTabs';
import PremiumInput from '../common/components/premium/PremiumInput';

const deviceFields = [
  { id: 'name', name: 'sharedName' },
  { id: 'uniqueId', name: 'deviceIdentifier' },
  { id: 'phone', name: 'sharedPhone' },
  { id: 'model', name: 'deviceModel' },
  { id: 'contact', name: 'deviceContact' },
  { id: 'geofenceIds', name: 'sharedGeofence' },
  { id: 'driverUniqueId', name: 'sharedDriver' },
  { id: 'motion', name: 'positionMotion' },
];

const PreferencesPage = () => {
  const { classes } = useSettingsStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const admin = useAdministrator();
  const readonly = useRestriction('readonly');

  const user = useSelector((state) => state.session.user);
  const [attributes, setAttributes] = useState(user.attributes);

  const versionApp = import.meta.env.VITE_APP_VERSION;
  const versionServer = useSelector((state) => state.session.server.version);
  const socket = useSelector((state) => state.session.socket);

  const [token, setToken] = useState(null);
  const [tokenExpiration, setTokenExpiration] = useState(dayjs().add(1, 'week').locale('en').format('YYYY-MM-DD'));

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  const mapStyles = useMapStyles();
  const mapOverlays = useMapOverlays();

  const positionAttributes = usePositionAttributes(t);
  const filter = createFilterOptions();

  const generateToken = useCatch(async () => {
    const expiration = dayjs(tokenExpiration, 'YYYY-MM-DD').toISOString();
    const response = await fetchOrThrow('/api/session/token', {
      method: 'POST',
      body: new URLSearchParams(`expiration=${expiration}`),
    });
    setToken(await response.text());
  });

  const alarms = useTranslationKeys((it) => it.startsWith('alarm')).map((it) => ({
    key: unprefixString('alarm', it),
    name: t(it),
  }));

  const handleSave = useCatch(async () => {
    const response = await fetchOrThrow(`/api/users/${user.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...user, attributes }),
    });
    dispatch(sessionActions.updateUser(await response.json()));
    navigate(-1);
  });

  const handleReboot = useCatch(async () => {
    const response = await fetch('/api/server/reboot', { method: 'POST' });
    throw Error(response.statusText);
  });

  // Tab configuration
  const tabs = [
    { label: t('mapTitle'), icon: <MapIcon /> },
    { label: t('deviceTitle'), icon: <DevicesOtherIcon /> },
    { label: t('sharedSound'), icon: <AudiotrackIcon /> },
    { label: t('userToken'), icon: <TokenIcon /> },
    { label: t('sharedInfoTitle'), icon: <InfoIcon /> },
  ];

  // Simple checkbox styles (no nested selectors issue)
  const checkboxSx = {
    color: tokens?.text?.secondary || '#8b949e',
    '&.Mui-checked': { color: tokens?.accent?.primary || '#00d4aa' },
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedPreferences']}>
      <Container maxWidth={false} className={classes.container}>
        <Paper
          sx={{
            backgroundColor: 'rgba(22, 27, 34, 0.6)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
            overflow: 'hidden',
          }}
        >
          {/* Premium Tabs */}
          <PremiumTabs
            tabs={tabs}
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          />

          {/* Tab Content Container */}
          <Box sx={{ px: 3, pb: 3 }}>
            {/* Tab 0: Mapa */}
            <PremiumTabPanel value={activeTab} index={0}>
              {!readonly && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <FormControl fullWidth size="small" className="premium-input">
                    <InputLabel>{t('mapActive')}</InputLabel>
                    <Select
                      label={t('mapActive')}
                      value={attributes.activeMapStyles?.split(',') || ['locationIqStreets', 'locationIqDark', 'openFreeMap']}
                      onChange={(e, child) => {
                        const clicked = mapStyles.find((s) => s.id === child.props.value);
                        if (clicked.available) {
                          setAttributes({ ...attributes, activeMapStyles: e.target.value.join(',') });
                        } else if (clicked.id !== 'custom') {
                          const query = new URLSearchParams({ attribute: clicked.attribute });
                          navigate(`/settings/user/${user.id}?${query.toString()}`);
                        }
                      }}
                      multiple
                    >
                      {mapStyles.map((style) => (
                        <MenuItem key={style.id} value={style.id}>
                          <Typography component="span" color={style.available ? 'textPrimary' : 'error'}>
                            {style.title}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" className="premium-input">
                    <InputLabel>{t('mapOverlay')}</InputLabel>
                    <Select
                      label={t('mapOverlay')}
                      value={attributes.selectedMapOverlay || ''}
                      onChange={(e) => {
                        const clicked = mapOverlays.find((o) => o.id === e.target.value);
                        if (!clicked || clicked.available) {
                          setAttributes({ ...attributes, selectedMapOverlay: e.target.value });
                        } else if (clicked.id !== 'custom') {
                          const query = new URLSearchParams({ attribute: clicked.attribute });
                          navigate(`/settings/user/${user.id}?${query.toString()}`);
                        }
                      }}
                    >
                      <MenuItem value="">{'\u00a0'}</MenuItem>
                      {mapOverlays.map((overlay) => (
                        <MenuItem key={overlay.id} value={overlay.id}>
                          <Typography component="span" color={overlay.available ? 'textPrimary' : 'error'}>
                            {overlay.title}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    freeSolo
                    options={Object.keys(positionAttributes)}
                    getOptionLabel={(option) => {
                      if (typeof option === 'object' && option.inputValue) {
                        return option.inputValue;
                      }
                      return positionAttributes[option]?.name || option;
                    }}
                    value={attributes.positionItems?.split(',') || ['fixTime', 'address', 'speed', 'totalDistance']}
                    onChange={(_, newValue) => {
                      setAttributes({
                        ...attributes,
                        positionItems: newValue.map((x) => (typeof x === 'string' ? x : x.inputValue)).join(','),
                      });
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      if (params.inputValue && !options.includes(params.inputValue)) {
                        filtered.push({ inputValue: params.inputValue, name: `${t('sharedAdd')} "${params.inputValue}"` });
                      }
                      return filtered;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.name ? option.name : (positionAttributes[option]?.name || option)}</li>
                    )}
                    renderInput={(params) => <TextField {...params} label={t('attributePopupInfo')} className="premium-input" />}
                  />

                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('mapLiveRoutes')}</InputLabel>
                      <Select
                        label={t('mapLiveRoutes')}
                        value={attributes.mapLiveRoutes || 'none'}
                        onChange={(e) => setAttributes({ ...attributes, mapLiveRoutes: e.target.value })}
                      >
                        <MenuItem value="none">{t('sharedDisabled')}</MenuItem>
                        <MenuItem value="selected">{t('deviceSelected')}</MenuItem>
                        <MenuItem value="all">{t('notificationAlways')}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('mapDirection')}</InputLabel>
                      <Select
                        label={t('mapDirection')}
                        value={attributes.mapDirection || 'selected'}
                        onChange={(e) => setAttributes({ ...attributes, mapDirection: e.target.value })}
                      >
                        <MenuItem value="none">{t('sharedDisabled')}</MenuItem>
                        <MenuItem value="selected">{t('deviceSelected')}</MenuItem>
                        <MenuItem value="all">{t('notificationAlways')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <FormGroup>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={attributes.hasOwnProperty('mapGeofences') ? attributes.mapGeofences : true}
                            onChange={(e) => setAttributes({ ...attributes, mapGeofences: e.target.checked })}
                            sx={checkboxSx}
                          />
                        }
                        label={t('attributeShowGeofences')}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={attributes.hasOwnProperty('mapFollow') ? attributes.mapFollow : false}
                            onChange={(e) => setAttributes({ ...attributes, mapFollow: e.target.checked })}
                            sx={checkboxSx}
                          />
                        }
                        label={t('deviceFollow')}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={attributes.hasOwnProperty('mapCluster') ? attributes.mapCluster : true}
                            onChange={(e) => setAttributes({ ...attributes, mapCluster: e.target.checked })}
                            sx={checkboxSx}
                          />
                        }
                        label={t('mapClustering')}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={attributes.hasOwnProperty('mapOnSelect') ? attributes.mapOnSelect : true}
                            onChange={(e) => setAttributes({ ...attributes, mapOnSelect: e.target.checked })}
                            sx={checkboxSx}
                          />
                        }
                        label={t('mapOnSelect')}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={attributes.hasOwnProperty('ui.menuCollapsedDefault') ? attributes['ui.menuCollapsedDefault'] : false}
                            onChange={(e) => setAttributes({ ...attributes, 'ui.menuCollapsedDefault': e.target.checked })}
                            sx={checkboxSx}
                          />
                        }
                        label={t('attributeUiDisableMenuCollapsed')}
                      />
                    </Box>
                  </FormGroup>
                </Box>
              )}
            </PremiumTabPanel>

            {/* Tab 1: Dispositivos */}
            <PremiumTabPanel value={activeTab} index={1}>
              {!readonly && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <SelectField
                    fullWidth
                    value={attributes.devicePrimary || 'name'}
                    onChange={(e) => setAttributes({ ...attributes, devicePrimary: e.target.value })}
                    data={deviceFields}
                    titleGetter={(it) => t(it.name)}
                    label={t('devicePrimaryInfo')}
                  />
                  <SelectField
                    fullWidth
                    value={attributes.deviceSecondary}
                    onChange={(e) => setAttributes({ ...attributes, deviceSecondary: e.target.value })}
                    data={deviceFields}
                    titleGetter={(it) => t(it.name)}
                    label={t('deviceSecondaryInfo')}
                  />
                </Box>
              )}
            </PremiumTabPanel>

            {/* Tab 2: Som */}
            <PremiumTabPanel value={activeTab} index={2}>
              {!readonly && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <SelectField
                    fullWidth
                    multiple
                    value={attributes.soundEvents?.split(',') || []}
                    onChange={(e) => setAttributes({ ...attributes, soundEvents: e.target.value.join(',') })}
                    endpoint="/api/notifications/types"
                    keyGetter={(it) => it.type}
                    titleGetter={(it) => t(prefixString('event', it.type))}
                    label={t('eventsSoundEvents')}
                  />
                  <SelectField
                    fullWidth
                    multiple
                    value={attributes.soundAlarms?.split(',') || ['sos']}
                    onChange={(e) => setAttributes({ ...attributes, soundAlarms: e.target.value.join(',') })}
                    data={alarms}
                    keyGetter={(it) => it.key}
                    label={t('eventsSoundAlarms')}
                  />
                </Box>
              )}
            </PremiumTabPanel>

            {/* Tab 3: Token */}
            <PremiumTabPanel value={activeTab} index={3}>
              <Box display="flex" flexDirection="column" gap={3}>
                <PremiumInput
                  label={t('userExpirationTime')}
                  type="date"
                  value={tokenExpiration}
                  onChange={(e) => {
                    setTokenExpiration(e.target.value);
                    setToken(null);
                  }}
                />
                <FormControl fullWidth>
                  <OutlinedInput
                    multiline
                    rows={6}
                    readOnly
                    type="text"
                    value={token || ''}
                    placeholder={t('userTokenPlaceholder') || 'Clique em gerar para criar um token...'}
                    endAdornment={
                      <InputAdornment position="end">
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <IconButton size="small" edge="end" onClick={generateToken} disabled={!!token}>
                            <CachedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={() => navigator.clipboard.writeText(token)}
                            disabled={!token}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    }
                    sx={{
                      backgroundColor: tokens.surface.elevated,
                      borderRadius: tokens.radius.md,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                    }}
                  />
                </FormControl>
              </Box>
            </PremiumTabPanel>

            {/* Tab 4: Informações */}
            <PremiumTabPanel value={activeTab} index={4}>
              {!readonly && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <PremiumInput
                      value={versionApp}
                      label={t('settingsAppVersion')}
                      disabled
                    />
                    <PremiumInput
                      value={versionServer || '-'}
                      label={t('settingsServerVersion')}
                      disabled
                    />
                    <PremiumInput
                      value={socket ? t('deviceStatusOnline') : t('deviceStatusOffline')}
                      label={t('settingsConnection')}
                      disabled
                    />
                  </Box>

                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/emulator')}
                      sx={{
                        borderColor: tokens.accent.primary,
                        color: tokens.accent.primary,
                        borderRadius: tokens.radius.md,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: tokens.accent.subtle,
                          borderColor: tokens.accent.primary,
                        },
                      }}
                    >
                      {t('sharedEmulator')}
                    </Button>
                    {admin && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleReboot}
                        sx={{
                          borderRadius: tokens.radius.md,
                          textTransform: 'none',
                        }}
                      >
                        {t('serverReboot')}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </PremiumTabPanel>
          </Box>
        </Paper>

        {/* Action Buttons */}
        {!readonly && (
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mt={3}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderColor: tokens.border.default,
                color: tokens.text.secondary,
                borderRadius: tokens.radius.md,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  borderColor: tokens.border.strong,
                  backgroundColor: tokens.surface.hover,
                },
              }}
            >
              {t('sharedCancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: tokens.accent.primary,
                borderRadius: tokens.radius.md,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: tokens.accent.hover,
                },
              }}
            >
              {t('sharedSave')}
            </Button>
          </Box>
        )}
      </Container>
    </PageLayout>
  );
};

export default PreferencesPage;
